"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import Link from "next/link";
import Image from "next/image";

interface Category {
  id: string;
  name: string;
  slug: string;
  color: string | null;
  articleCount: number;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  featuredImageAlt: string | null;
  authorName: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number | null;
  categories: { id: string; name: string; slug: string; color: string | null }[];
}

function formatDate(date: string | null): string {
  if (!date) return "";
  let d = new Date(date);
  if (isNaN(d.getTime()) && date.includes(" ") && !date.includes("T")) {
    d = new Date(date.replace(" ", "T") + "Z");
  }
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ImagePlaceholder() {
  return (
    <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)]">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="opacity-30">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </div>
  );
}

function HeroArticle({ article, onCategoryClick }: { article: Article; onCategoryClick: (slug: string) => void }) {
  return (
    <div className="mb-10">
      <Link
        href={`/blog/${article.slug}`}
        className="group block rounded-2xl overflow-hidden border border-[var(--border-color)] bg-[var(--bg-card)] hover:shadow-xl transition-all duration-300 hover:border-[var(--brand)]/30"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative aspect-video md:aspect-auto md:min-h-[340px] overflow-hidden bg-[var(--bg-secondary)]">
            {article.featuredImage ? (
              <Image
                src={article.featuredImage}
                alt={article.featuredImageAlt || article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                width={800}
                height={450}
                unoptimized
                priority
              />
            ) : (
              <ImagePlaceholder />
            )}
            <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--brand)] text-white shadow-sm">
              Featured
            </span>
          </div>
          <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-center">
            {article.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {article.categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onCategoryClick(cat.slug);
                    }}
                    className="px-2.5 py-0.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:ring-offset-1"
                    style={{
                      backgroundColor: `${cat.color || "#6B7280"}20`,
                      color: cat.color || "#6B7280",
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[var(--text-primary)] group-hover:text-[var(--brand)] transition-colors mb-3 leading-tight">
              {article.title}
            </h2>
            {article.excerpt && (
              <p className="text-[var(--text-muted)] mb-5 line-clamp-3 text-base leading-relaxed">
                {article.excerpt}
              </p>
            )}
            <div className="flex items-center gap-3 text-sm text-[var(--text-muted)] mb-4">
              {article.authorName && <span className="font-medium text-[var(--text-primary)]">{article.authorName}</span>}
              {article.authorName && article.publishedAt && <span aria-hidden="true">&middot;</span>}
              {article.publishedAt && <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>}
              {article.readingTimeMinutes && (
                <>
                  <span aria-hidden="true">&middot;</span>
                  <span>{article.readingTimeMinutes} min read</span>
                </>
              )}
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--brand)] group-hover:gap-2.5 transition-all">
              Read article
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}

function ArticleCard({ article, onCategoryClick }: { article: Article; onCategoryClick: (slug: string) => void }) {
  return (
    <div className="group flex flex-col rounded-xl overflow-hidden border border-[var(--border-color)] bg-[var(--bg-card)] hover:shadow-lg hover:border-[var(--brand)]/20 transition-all duration-300">
      <Link href={`/blog/${article.slug}`} className="block">
        <div className="aspect-video overflow-hidden bg-[var(--bg-secondary)]">
          {article.featuredImage ? (
            <Image
              src={article.featuredImage}
              alt={article.featuredImageAlt || article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              width={400}
              height={225}
              unoptimized
            />
          ) : (
            <ImagePlaceholder />
          )}
        </div>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        {article.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {article.categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCategoryClick(cat.slug);
                }}
                className="px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:ring-offset-1"
                style={{
                  backgroundColor: `${cat.color || "#6B7280"}20`,
                  color: cat.color || "#6B7280",
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}
        <Link href={`/blog/${article.slug}`} className="block flex-1">
          <h2 className="text-lg font-semibold text-[var(--text-primary)] group-hover:text-[var(--brand)] transition-colors mb-2 line-clamp-2 leading-snug">
            {article.title}
          </h2>
          {article.excerpt && (
            <p className="text-sm text-[var(--text-muted)] line-clamp-2 mb-4 leading-relaxed">
              {article.excerpt}
            </p>
          )}
        </Link>
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-[var(--border-color)]/50">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            {article.authorName && <span className="font-medium">{article.authorName}</span>}
            {article.authorName && article.publishedAt && <span aria-hidden="true">&middot;</span>}
            {article.publishedAt && <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>}
          </div>
          {article.readingTimeMinutes && (
            <span className="text-xs text-[var(--text-muted)]">{article.readingTimeMinutes} min</span>
          )}
        </div>
      </div>
    </div>
  );
}

function BlogListingInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "");
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "12" });
    if (search) params.set("search", search);
    if (activeCategory) params.set("category", activeCategory);

    try {
      const res = await fetch(`/api/blog?${params}`);
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch {
      console.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  }, [page, search, activeCategory]);

  useEffect(() => {
    fetch("/api/blog/categories")
      .then((res) => res.json())
      .then((data) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (activeCategory) params.set("category", activeCategory);
    if (page > 1) params.set("page", String(page));
    const queryString = params.toString();
    router.replace(`/blog${queryString ? `?${queryString}` : ""}`, { scroll: false });
  }, [search, activeCategory, page, router]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleCategoryClick = (slug: string) => {
    setActiveCategory(activeCategory === slug ? "" : slug);
    setPage(1);
  };

  const clearAllFilters = () => {
    setSearch("");
    setSearchInput("");
    setActiveCategory("");
    setPage(1);
  };

  const isFirstPage = page === 1 && !search && !activeCategory;
  const heroArticle = isFirstPage && articles.length > 0 ? articles[0] : null;
  const gridArticles = heroArticle ? articles.slice(1) : articles;
  const hasActiveFilters = !!(search || activeCategory);
  const visibleCategories = categories.filter(c => c.articleCount > 0);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 xl:px-10 pt-8 xl:pt-14">
      <div className="mb-8 space-y-4">
        <form onSubmit={handleSearch} className="relative flex gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search articles..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-primary)] text-sm placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-transparent transition-shadow"
              aria-label="Search blog articles"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); if (search) { setSearch(""); setPage(1); } }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                aria-label="Clear search"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-5 py-2.5 rounded-lg bg-[var(--brand)] text-white text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:ring-offset-2"
          >
            Search
          </button>
        </form>

        {visibleCategories.length > 0 && (
          <div className="relative">
            <select
              value={activeCategory}
              onChange={(e) => {
                setActiveCategory(e.target.value);
                setPage(1);
              }}
              className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-transparent transition-shadow cursor-pointer"
              aria-label="Filter by topic"
            >
              <option value="">All Topics</option>
              {visibleCategories.map((cat) => (
                <option key={cat.id} value={cat.slug}>
                  {cat.name} ({cat.articleCount})
                </option>
              ))}
            </select>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-8">
          <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] overflow-hidden animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="aspect-video md:min-h-[340px] bg-[var(--bg-secondary)]" />
              <div className="p-8 space-y-4 flex flex-col justify-center">
                <div className="h-3 bg-[var(--bg-secondary)] rounded w-1/4" />
                <div className="h-6 bg-[var(--bg-secondary)] rounded w-3/4" />
                <div className="h-4 bg-[var(--bg-secondary)] rounded w-full" />
                <div className="h-4 bg-[var(--bg-secondary)] rounded w-2/3" />
                <div className="h-3 bg-[var(--bg-secondary)] rounded w-1/3 mt-2" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] overflow-hidden animate-pulse">
                <div className="aspect-video bg-[var(--bg-secondary)]" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-[var(--bg-secondary)] rounded w-1/3" />
                  <div className="h-4 bg-[var(--bg-secondary)] rounded w-3/4" />
                  <div className="h-3 bg-[var(--bg-secondary)] rounded w-full" />
                  <div className="h-3 bg-[var(--bg-secondary)] rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 px-6">
          <div className="max-w-md mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[var(--bg-card)] border border-[var(--border-color)] mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--brand)]">
                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
                {hasActiveFilters ? (
                  <>
                    <line x1="9" y1="10" x2="15" y2="10" />
                  </>
                ) : (
                  <>
                    <line x1="12" y1="6" x2="12" y2="10" />
                    <line x1="12" y1="14" x2="12.01" y2="14" />
                  </>
                )}
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
              {hasActiveFilters ? "No articles found" : "Coming soon"}
            </h3>
            <p className="text-[var(--text-muted)] mb-6 leading-relaxed">
              {hasActiveFilters
                ? "We couldn\u2019t find any articles matching your criteria. Try different search terms or browse all topics."
                : "We\u2019re working on insightful content about cooperatives, ESOPs, and democratic workplaces. Check back soon!"}
            </p>
            {hasActiveFilters ? (
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--brand)] text-white text-sm font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[var(--brand)] focus:ring-offset-2"
              >
                Clear all filters
              </button>
            ) : (
              <Link
                href="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[var(--border-color)] text-[var(--text-primary)] text-sm font-medium hover:bg-[var(--bg-card)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
              >
                Explore EconChangers
              </Link>
            )}
          </div>
        </div>
      ) : (
        <>
          {search && (
            <p className="text-sm text-[var(--text-muted)] mb-6" role="status" aria-live="polite">
              {total} result{total !== 1 ? "s" : ""} for &ldquo;{search}&rdquo;
              {activeCategory && (
                <span> in <strong className="text-[var(--text-primary)]">{categories.find(c => c.slug === activeCategory)?.name}</strong></span>
              )}
            </p>
          )}

          {heroArticle && (
            <HeroArticle article={heroArticle} onCategoryClick={handleCategoryClick} />
          )}

          {gridArticles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {gridArticles.map((article) => (
                <ArticleCard key={article.id} article={article} onCategoryClick={handleCategoryClick} />
              ))}
            </div>
          )}
        </>
      )}

      {totalPages > 1 && (
        <nav className="flex justify-center items-center gap-3 mt-12 mb-2" aria-label="Blog pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--border-color)] text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--bg-card)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
            aria-label="Go to previous page"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Previous
          </button>
          <span className="text-sm text-[var(--text-muted)] px-2">
            Page <strong className="text-[var(--text-primary)]">{page}</strong> of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--border-color)] text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--bg-card)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand)]"
            aria-label="Go to next page"
          >
            Next
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </nav>
      )}
    </div>
  );
}

export default function BlogListingContent() {
  return (
    <Suspense fallback={<div className="text-center py-12 text-[var(--text-muted)]">Loading...</div>}>
      <BlogListingInner />
    </Suspense>
  );
}
